import { Injectable } from '@angular/core';
import {
	AbstractCRUDService,
	AlarisApiService,
	AlarisConfigService
} from '@campaign-portal/components-library';
import { ReadRequest, ReadResponse } from '@campaign-portal/namespace/common/implementations';
import { Filter, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { ExportService } from '@helpers/api/export.service';
import {
	MDTaskCreateRequest,
	MDTaskCreateResponse,
	MDTaskDeleteRequest,
	MDTaskDeleteResponse,
	MDTaskReadRequest,
	MDTaskReadResponse
} from '@helpers/types/hlr';
import { MDTask } from '@helpers/types/models';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { ManualDippingFieldsService } from './manual-dipping.fields.service';

@Injectable({
	providedIn: 'root'
})
export class ManualDippingService implements AbstractCRUDService {
	readonly entity = '';
	readonly title = '';
	readonly loading$ = new BehaviorSubject<boolean>(false);
	readonly refresh$ = new BehaviorSubject<boolean>(false);
	readonly update = this.create;

	lastMdTask: MDTask | null = null;

	constructor(
		private readonly api: AlarisApiService,
		private readonly exportService: ExportService,
		private readonly config: AlarisConfigService,
		private readonly fields: ManualDippingFieldsService
	) {}

	read(params: ReadRequest): Observable<ReadResponse<MDTask[]>> {
		const parsedParams: MDTaskReadRequest = this.prepareParams(params);
		return this.api.loader<MDTaskReadResponse>('MDTasks.Read', parsedParams, this.loading$).pipe(
			map((resp) => {
				this.refresh$.next(false);
				return {
					Success: !!resp.data,
					Total: resp.length,
					Data: resp.data
				};
			})
		);
	}

	create(params: MDTaskCreateRequest): Observable<MDTaskCreateResponse> {
		return this.api.loader<MDTaskCreateResponse>('MDTasks.Create', params, this.loading$);
	}

	delete(params: MDTaskDeleteRequest): Observable<MDTaskDeleteResponse> {
		return this.api.loader<MDTaskDeleteResponse>('MDTasks.Delete', params, this.loading$);
	}

	export(total: number, params: RPCRequestParams): Observable<void> {
		params = {
			...params,
			Paging: { Skip: 0, Take: total }
		};
		return this.exportService.export(
			this.config.api,
			'MDTasks.Read',
			this.prepareParams(params),
			this.fields.headers,
			'manual_dipping'
		);
	}

	private prepareParams(params: RPCRequestParams): MDTaskReadRequest {
		return {
			offset: params.Paging?.Skip ?? 0,
			limit: params.Paging?.Take ?? 25,
			active: 'md_task_id',
			direction: 'desc',
			filter: params.Filters
				? params.Filters.map((filter) => {
					return this.parseFilterField(filter);
				}) : []
		};
	}

	private parseFilterField(filter: Filter): { key: string; value: string | number } {
		const variable = filter.Field;
		const value = filter.Value;
		switch (variable) {
			case 'mdTaskID': {
				const normalizedId = String(value).replace(/^dd-/i, '');
				return { key: 'md_task_id', value: normalizedId };
			}
			case 'status': {
				return { key: variable, value: (value as { name: string; value: string }).value };
			}
			case 'author': {
				return { key: 'author_id', value: (value as { name: string; value: number }).value + '' };
			}
			default: {
				return { key: variable, value: value + '' };
			}
		}
	}
}
